import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faTimes, 
  faPencilAlt,
  faTrash,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import useSiteData from '../../hooks/useSiteData';

const EditAbout = () => {
  const { data, isLoading, refetch } = useSiteData();
  const [formData, setFormData] = useState({
    storyData: {
      title: '',
      imageUrl: '',
      paragraphs: ['']
    }
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (data?.about?.storyData) {
      setFormData({
        storyData: {
          title: data.about.storyData.title || '',
          imageUrl: data.about.storyData.imageUrl || '',
          paragraphs: data.about.storyData.paragraphs || ['']
        }
      });
    }
  }, [data]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      storyData: {
        ...prev.storyData,
        [field]: value
      }
    }));
  };

  const handleParagraphChange = (index, value) => {
    setFormData(prev => {
      const newParagraphs = [...prev.storyData.paragraphs];
      newParagraphs[index] = value;
      return {
        storyData: {
          ...prev.storyData,
          paragraphs: newParagraphs
        }
      };
    });
  };

  const addParagraph = () => {
    setFormData(prev => ({
      storyData: {
        ...prev.storyData,
        paragraphs: [...prev.storyData.paragraphs, '']
      }
    }));
  };

  const removeParagraph = (index) => {
    setFormData(prev => ({
      storyData: {
        ...prev.storyData,
        paragraphs: prev.storyData.paragraphs.filter((_, i) => i !== index)
      }
    }));
  };

  const handleSubmit = async () => {
    try {
      // Validate data
      if (!formData.storyData.title.trim()) {
        toast.error('Title is required');
        return;
      }

      if (!formData.storyData.imageUrl.trim()) {
        toast.error('Image URL is required');
        return;
      }

      // Filter out empty paragraphs
      const nonEmptyParagraphs = formData.storyData.paragraphs.filter(p => p.trim());
      if (nonEmptyParagraphs.length === 0) {
        toast.error('At least one paragraph is required');
        return;
      }

      const docRef = doc(db, "about", "singleton");
      await setDoc(docRef, {
        storyData: {
          ...formData.storyData,
          paragraphs: nonEmptyParagraphs
        }
      });
      
      toast.success('About section updated successfully!');
      setError('');
      setIsEditing(false);
      refetch();
    } catch (error) {
      console.error('Error updating about section:', error);
      setError('Failed to update about section.');
      toast.error('Failed to update about section.');
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  const renderReadOnlyMode = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Our Story</h2>
        <button
          onClick={() => setIsEditing(true)}
          className="px-4 py-2 text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 flex items-center"
        >
          <FontAwesomeIcon icon={faPencilAlt} className="mr-2" />
          Edit
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <h3 className="font-medium text-gray-700">Title</h3>
          <p className="text-gray-600">{formData.storyData.title}</p>
        </div>

        <div>
          <h3 className="font-medium text-gray-700">Image</h3>
          {formData.storyData.imageUrl && (
            <img 
              src={formData.storyData.imageUrl} 
              alt="Story" 
              className="mt-2 max-w-md rounded-lg"
            />
          )}
        </div>

        <div>
          <h3 className="font-medium text-gray-700">Story</h3>
          {formData.storyData.paragraphs.map((paragraph, index) => (
            <p key={index} className="text-gray-600 mt-2">{paragraph}</p>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex-1 min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold mb-6">Edit About Section</h1>
        
        {error && (
          <div className="bg-red-50 text-red-500 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}
        
        {isEditing ? (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.storyData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Enter story title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Image URL
                  </label>
                  <input
                    type="text"
                    value={formData.storyData.imageUrl}
                    onChange={(e) => handleChange('imageUrl', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Enter image URL"
                  />
                  {formData.storyData.imageUrl && (
                    <img 
                      src={formData.storyData.imageUrl} 
                      alt="Preview" 
                      className="mt-2 max-w-md rounded-lg"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Story Paragraphs
                  </label>
                  {formData.storyData.paragraphs.map((paragraph, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <textarea
                        value={paragraph}
                        onChange={(e) => handleParagraphChange(index, e.target.value)}
                        className="w-full px-3 py-2 border rounded-md"
                        rows="3"
                        placeholder={`Enter paragraph ${index + 1}`}
                      />
                      {formData.storyData.paragraphs.length > 1 && (
                        <button
                          onClick={() => removeParagraph(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={addParagraph}
                    className="mt-2 text-blue-600 hover:text-blue-700 flex items-center"
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Add Paragraph
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <FontAwesomeIcon icon={faTimes} className="mr-2" />
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                <FontAwesomeIcon icon={faSave} className="mr-2" />
                Save Changes
              </button>
            </div>
          </div>
        ) : (
          renderReadOnlyMode()
        )}
      </div>
    </div>
  );
};

export default EditAbout;
